import AsyncCreatableSelect from 'react-select/async-creatable';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Add, getMedicineBySearch, getMedicineById, Details } from '../modules/admin/services/medicine.services';
import { capitalizeFirstLetter } from './commonfunction';
import { ROLE } from './Constants';

const MedicineSelect = ({ index, form }) => {
  const [options, setOptions] = useState([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [fetchedIds, setFetchedIds] = useState(new Set());
  const debounceTimer = useRef(null);

  const selectedId = form?.values?.medications?.[index]?.medicine_id;
  const selectedName = form?.values?.medications?.[index]?.medicine_name;

  // Fetch medicine name if only ID is available (Edit Mode)
  useEffect(() => {
    const fetchNameById = async () => {
      if (selectedId && !selectedName && !fetchedIds.has(selectedId)) {
        try {
          const response = await Details(selectedId, ROLE.DOCTOR);
          const medicine = response?.data;

          if (medicine?.name) {
            const name = capitalizeFirstLetter(medicine.name);
            form.setFieldValue(`medications[${index}].medicine_name`, name);

            const newOption = { value: medicine._id, label: name };
            setOptions((prev) => {
              const exists = prev.some((opt) => opt.value === newOption.value);
              return exists ? prev : [...prev, newOption];
            });

            setFetchedIds((prev) => new Set([...prev, selectedId]));
          }
        } catch (error) {
          console.error('Error fetching medicine name by ID:', error);
        } finally {
          setIsLoadingInitial(false);
        }
      } else {
        setIsLoadingInitial(false);
      }
    };

    fetchNameById();
  }, [selectedId, selectedName, index, form, fetchedIds]);

  useEffect(() => {
  const fetchDefaultOptions = async () => {
    try {
      const response = await getMedicineBySearch('', ROLE.DOCTOR); // Fetch without query
      const data = response?.data || [];

      const defaultOpts = data.map((item) => ({
        value: item._id,
        label: capitalizeFirstLetter(item.name),
      }));

      setOptions(defaultOpts);
    } catch (error) {
      console.error('Error loading default medicine options:', error);
    }
  };

  fetchDefaultOptions();
}, []);


  // Debounced search
  const loadOptions = useCallback((inputValue, callback) => {
    if (!inputValue.trim()) {
      callback([]);
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await getMedicineBySearch(inputValue, ROLE.DOCTOR);
        const data = response?.data || [];

        const newOptions = data.map((item) => ({
          value: item._id,
          label: capitalizeFirstLetter(item.name),
        }));

        setOptions((prev) => {
          const existingIds = new Set(prev.map((opt) => opt.value));
          return [...prev, ...newOptions.filter((opt) => !existingIds.has(opt.value))];
        });

        callback(newOptions);
      } catch (error) {
        console.error('Error fetching medicine:', error);
        callback([]);
      }
    }, 500);
  }, []);

  // Create new medicine
  const handleCreate = async (inputValue) => {
      try {
          const response = await Add({ name: inputValue }, ROLE.DOCTOR);
          const newMedicine = response.data;

          if (!newMedicine || !newMedicine._id || !newMedicine.name) {
              console.error("Invalid medicine data returned from creation.");
              return;
          }

          const newOption = {
              value: newMedicine._id,
              label: capitalizeFirstLetter(newMedicine.name),
          };

          // Add to options if not already present
          setOptions((prev) => {
              const exists = prev.some((opt) => opt.value === newOption.value);
              return exists ? prev : [...prev, newOption];
          });

          // Track fetched ID
          setFetchedIds((prev) => new Set(prev).add(newOption.value));

          // ✅ Set the newly created medicine as selected
          form.setFieldValue(`medications[${index}].medicine_id`, newOption.value);
          form.setFieldValue(`medications[${index}].medicine_name`, newOption.label);
      } catch (error) {
          console.error("Failed to create new medicine:", error);
      }
  };


  const selectedValue =
    selectedId && selectedName
      ? { value: selectedId, label: capitalizeFirstLetter(selectedName) }
      : null;

  return (
    <div className="col-md-2 add-more-medication-item">
      <label className="form-label">Medicine</label>
      <AsyncCreatableSelect
        cacheOptions
        defaultOptions={options}
        options={options} // ✅ Ensures new options are reflected immediately
        isLoading={isLoadingInitial}
        loadOptions={loadOptions}
        value={selectedValue}
        onChange={(selectedOption) => {
            form.setFieldValue(`medications[${index}].medicine_id`, selectedOption?.value || '');
            form.setFieldValue(`medications[${index}].medicine_name`, selectedOption?.label || '');
        }}
        onCreateOption={handleCreate}
        placeholder="Search or create"
        isClearable
    />  

      {form?.touched?.medications?.[index]?.medicine_id &&
        form?.errors?.medications?.[index]?.medicine_id && (
          <div className="text-danger">
            {form?.errors?.medications[index]?.medicine_id}
          </div>
        )}
    </div>
  );
};

export default MedicineSelect;
